import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { ShieldCheck, Info } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const shopApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

const PAGE_ORDER = ["dropshipping_disclosure", "terms", "privacy", "refund", "shipping", "imprint"];

/** Public footer with links to whichever legal pages the operator has approved.
 *  If none are approved, still shows a short dropshipping disclosure banner
 *  (statutory transparency requirement in CH/EU consumer law). */
export function LegalFooter() {
  const { lang } = useLang();
  const [published, setPublished] = useState([]);

  useEffect(() => {
    shopApi.get("/shop/legal").then((r) => {
      setPublished(r.data.pages || []);
    }).catch(() => setPublished([]));
  }, []);

  const has = (pt) => published.find((p) => p.page_type === pt);
  const sorted = PAGE_ORDER.filter(has).concat(published.map((p) => p.page_type).filter((pt) => !PAGE_ORDER.includes(pt)));

  return (
    <footer className="bg-[color:var(--ss-bg-soft)] border-t border-[color:var(--ss-border)] mt-16" data-testid="legal-footer">
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-4">
        {/* Mandatory dropshipping transparency banner — always shown */}
        <div className="flex items-start gap-2 text-[11px] text-[color:var(--ss-ink-muted)] leading-relaxed border border-[color:var(--ss-border-strong)] p-4 rounded bg-white">
          <ShieldCheck size={14} className="text-[color:var(--ss-gold)] shrink-0 mt-0.5" />
          <div>
            <div className="ss-mono text-[10px] uppercase text-[color:var(--ss-gold-lo)] mb-1 tracking-widest">
              {t(lang, "footer_transparency_title")}
            </div>
            <p>{t(lang, "footer_transparency_body")}</p>
            {has("dropshipping_disclosure") && (
              <Link
                to="/legal/dropshipping_disclosure"
                className="inline-flex items-center gap-1 text-[color:var(--ss-gold)] mt-1 hover:underline"
                data-testid="footer-dropship-link"
              >
                <Info size={10} /> {t(lang, "footer_read_full")}
              </Link>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 gap-y-2 justify-between items-center pt-2">
          <div className="ss-mono text-[10px] uppercase tracking-widest text-[color:var(--ss-ink-muted)]">
            ShopSchoen.CH · Swiss curation · CHF · Powered by EMRKT.CH
          </div>
          <nav className="flex flex-wrap gap-x-4 gap-y-1 items-center" data-testid="legal-nav">
            <Link
              to="/shop/withdrawal"
              data-testid="footer-withdrawal-link"
              className="ss-mono text-[10px] uppercase text-[color:var(--ss-gold)] hover:underline tracking-widest"
            >
              14-day withdrawal
            </Link>
            {sorted.map((pt) => (
              <Link
                key={pt}
                to={`/legal/${pt}`}
                className="ss-mono text-[10px] uppercase text-[color:var(--ss-ink-muted)] hover:text-[color:var(--ss-ink)] transition-colors tracking-widest"
                data-testid={`legal-link-${pt}`}
              >
                {t(lang, `legal_pt_${pt}`)}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  );
}
